import { reactive, readonly, toRefs, watch } from 'vue';
import { omit } from 'lodash';
import { agwClient } from '@tfpl/api-client';
import { useLogger } from '@/composables/useLogger';
import { useConfig } from '@/composables/useConfig';

// A single prompt a user saved in the AI Chat prompt library (TFPL-2066).
// Private per user — see aiChatPrompts below for how it is (not) persisted
// outside the authoritative server-side erpSettings.
export type SavedPrompt = {
  id: string;
  title: string;
  text: string;
  createdAt: string;
};

export type PersonalSettingsState = {
  favoriteMenuItems: (number | string)[];
  darkMode: boolean;
  openInNewWindow: boolean;
  agGridTablesColumnsDef: Record<string, unknown>;
  agGridTablesOptions: Record<string, unknown>;
  selectedTenantCode: string;
  language: string;
  menuCollapsed: boolean;
  aiChatPrompts: SavedPrompt[];
};

export const DEFAULT_PERSONAL_SETTINGS: PersonalSettingsState = {
  favoriteMenuItems: [],
  darkMode: true,
  openInNewWindow: false,
  agGridTablesColumnsDef: {},
  agGridTablesOptions: {},
  selectedTenantCode: 'TF',
  language: 'en',
  menuCollapsed: true,
  aiChatPrompts: [],
};

const STORAGE_KEY = 'personalSettings';

const { logger } = useLogger();
const { getConfig } = useConfig();

const loadFromLocalStorage = (): PersonalSettingsState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // aiChatPrompts is never read from localStorage: the cache key is not
    // bound to a user, so a shared browser would leak one user's prompts to
    // the next. The server (erpSettings) is the only source of truth.
    return { ...DEFAULT_PERSONAL_SETTINGS, ...parsed, aiChatPrompts: [] } as PersonalSettingsState;
  } catch {
    logger.warn('Failed to parse personal settings from localStorage');
    return null;
  }
};

const state = reactive<PersonalSettingsState>({
  ...DEFAULT_PERSONAL_SETTINGS,
  ...loadFromLocalStorage(),
});

// sync ag-grid theme data attribute initially
if (typeof document !== 'undefined') {
  document.body.dataset.agThemeMode = state.darkMode ? 'dark' : 'light';
}

// True once a GET of the server-side erpSettings has succeeded in the current
// session. Guards savePersonalSettings (see below) and is intentionally not
// part of the reactive/persisted state.
let serverSettingsLoaded = false;

// persist to localStorage on every change, except aiChatPrompts (see above)
watch(
  () => omit(state, ['aiChatPrompts']),
  (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      logger.warn('Failed to save personal settings to localStorage');
    }
  },
  { deep: true },
);

const addOrRemove = <T>(arr: T[], item: T): T[] =>
  arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

export const usePersonalSettings = () => {
  const setDarkMode = (darkMode: boolean) => {
    state.darkMode = darkMode;
    if (typeof document !== 'undefined') {
      document.body.dataset.agThemeMode = darkMode ? 'dark' : 'light';
    }
  };

  const toggleFavouriteMenuItem = (favoriteMenuItemId: number | string) => {
    state.favoriteMenuItems = addOrRemove(state.favoriteMenuItems || [], favoriteMenuItemId);
  };

  const setSelectedTenantCode = (tenantCode: string) => {
    state.selectedTenantCode = tenantCode;
  };

  const setOpenInNewWindow = (openInNewWindow: boolean) => {
    state.openInNewWindow = openInNewWindow;
  };

  const setAgGridTablesColumnsDef = (payload: Record<string, unknown>) => {
    state.agGridTablesColumnsDef = {
      ...state.agGridTablesColumnsDef,
      ...payload,
    };
  };

  const setAgGridTablesOptions = (payload: Record<string, unknown>) => {
    state.agGridTablesOptions = {
      ...state.agGridTablesOptions,
      ...payload,
    };
  };

  const setLanguage = (language: string) => {
    state.language = language;
  };

  const setMenuCollapsed = (collapsed: boolean) => {
    state.menuCollapsed = collapsed;
  };

  const setPersonalSettings = (payload: PersonalSettingsState) => {
    state.favoriteMenuItems = payload.favoriteMenuItems;
    state.darkMode = payload.darkMode;
    state.openInNewWindow = payload.openInNewWindow;
    state.agGridTablesColumnsDef = payload.agGridTablesColumnsDef || {};
    state.agGridTablesOptions = payload.agGridTablesOptions || {};
    state.selectedTenantCode = payload.selectedTenantCode || state.selectedTenantCode;
    if (typeof document !== 'undefined') {
      document.body.dataset.agThemeMode = payload.darkMode ? 'dark' : 'light';
    }
    state.language = payload.language || state.language;
    state.menuCollapsed = payload.menuCollapsed ?? DEFAULT_PERSONAL_SETTINGS.menuCollapsed;
    state.aiChatPrompts = Array.isArray(payload.aiChatPrompts) ? payload.aiChatPrompts : [];
  };

  const savePersonalSettings = async () => {
    // Saving before a GET has completed for this session would overwrite the
    // server's erpSettings (including aiChatPrompts, which never round-trips
    // through localStorage) with data we have not actually confirmed is ours —
    // e.g. a prior user's stale local state after a failed load. Skip the PUT
    // until we know what the server currently holds.
    if (!serverSettingsLoaded) {
      logger.warn('Skipping savePersonalSettings: personal settings were not loaded from the server yet');
      return;
    }
    try {
      await agwClient.TfAgwPersonalSettingsService.updatePersonalSettings({
        path: {
          tenant: getConfig().TENANT,
        },
        body: {
          settings: JSON.stringify({ ...state }),
          app: 'ERP',
        },
        throwOnError: true,
      });
    } catch (e) {
      logger.error(e);
    }
  };

  const loadPersonalSettings = async () => {
    try {
      const { data: response } = await agwClient.TfAgwPersonalSettingsService.getPersonalSettings({
        path: {
          tenant: getConfig().TENANT,
        },
        throwOnError: true,
      });
      const raw = response?.data?.erpSettings;
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as Partial<PersonalSettingsState>;
          setPersonalSettings({ ...DEFAULT_PERSONAL_SETTINGS, ...parsed });
        } catch {
          logger.warn('Failed to parse ERP personal settings from API');
          state.aiChatPrompts = [];
        }
      } else {
        // New user, no erpSettings saved yet — no prompts either.
        state.aiChatPrompts = [];
      }
      serverSettingsLoaded = true;
    } catch (e) {
      logger.error(e);
      // GET failed: we don't know the current user's real prompt library, so
      // clear whatever is in memory (possibly a previous user's) rather than
      // show or risk persisting it. See savePersonalSettings above.
      state.aiChatPrompts = [];
      serverSettingsLoaded = false;
    }
  };

  const addAiChatPrompt = ({ title, text }: { title: string; text: string }) => {
    const prompt: SavedPrompt = {
      id: crypto.randomUUID(),
      title,
      text,
      createdAt: new Date().toISOString(),
    };
    state.aiChatPrompts = [...state.aiChatPrompts, prompt];
    return savePersonalSettings();
  };

  const updateAiChatPrompt = (id: string, { title, text }: { title: string; text: string }) => {
    if (!state.aiChatPrompts.some((p) => p.id === id)) return Promise.resolve();
    state.aiChatPrompts = state.aiChatPrompts.map((p) => (p.id === id ? { ...p, title, text } : p));
    return savePersonalSettings();
  };

  const removeAiChatPrompt = (id: string) => {
    state.aiChatPrompts = state.aiChatPrompts.filter((p) => p.id !== id);
    return savePersonalSettings();
  };

  return {
    ...toRefs(readonly(state)),
    // state refs
    state,
    // actions
    setDarkMode,
    toggleFavouriteMenuItem,
    setSelectedTenantCode,
    setOpenInNewWindow,
    setAgGridTablesColumnsDef,
    setAgGridTablesOptions,
    setPersonalSettings,
    savePersonalSettings,
    loadPersonalSettings,
    setLanguage,
    setMenuCollapsed,
    addAiChatPrompt,
    updateAiChatPrompt,
    removeAiChatPrompt,
  };
};
